<template>
    <div class="chart-container">
        <!-- 주석 처리된 CommonTitlePanel 컴포넌트 (더 이상 사용하지 않음) -->
        <div class="titleContainer">
            <div style="display: flex; flex-direction: column">
                <div style="display: flex; justify-content: center; align-items: center">
                    <div class="common-title">{{ "건물온실가스총량제" }}</div>
                    <span
                        style="font-size: 14px; margin-left: 10px; cursor: pointer"
                        @click="() => $refs.emissionCapModal.show()"
                    >
                        <i class="fa fa-question-circle"></i>
                    </span>
                </div>
                <small class="common-subtitle">
                    {{ subtitleText }}
                </small>
            </div>
        </div>
        <div ref="gaugeChart" class="chart" :style="{ height: chartHeight + 'vh' }"></div>

        <b-modal ref="emissionCapModal" hide-footer @hidden="() => $refs.emissionCapModal.hide()">
            <template #modal-header>
                <div class="d-flex justify-content-between align-items-center w-100">
                    <h5 class="mb-0">{{ "서울시 온실가스 총량제 기준" }}</h5>
                    <div class="d-flex justify-content-end" style="font-size: 14px">
                        <span class="setting-icon">
                            <i class="fas fa-times" @click="() => $refs.emissionCapModal.hide()"></i>
                        </span>
                    </div>
                </div>
            </template>
            <template #default>
                <table class="table table-bordered table-striped text-center">
                    <thead class="thead-dark">
                        <tr>
                            <th scope="col">{{ "유형" }}</th>
                            <th scope="col">{{ "건축법상 용도" }}</th>
                            <th scope="col">{{ "표준 배출기준 (’17~’19년 평균, tCO₂/㎡)" }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(grade, index) in buildingEmissions" :key="`EMISSION-CAP-${index}`">
                            <td>{{ grade.type }}</td>
                            <td>{{ grade.usage }}</td>
                            <td>{{ grade.emission }}</td>
                        </tr>
                    </tbody>
                </table>
            </template>
        </b-modal>
    </div>
</template>

<script>
import * as echarts from "echarts";
// import CommonTitlePanel from "../panels/CommonTitlePanel.vue";

export default {
    props: ["data", "chartHeight", "timeLabel"],
    // components: { CommonTitlePanel },
    data() {
        return {
            chart: null,
            resizeHandler: null,
            renderRetryCount: 0,
            maxRetryCount: 5,
            buildingEmissions: [
                { type: "가", usage: "제1·2종 근린생활시설, 판매시설", emission: 0.08 },
                { type: "나", usage: "업무시설", emission: 0.06 },
                { type: "다", usage: "교육연구·노유자시설", emission: 0.047 },
                { type: "라", usage: "문화 및 집회시설", emission: 0.065 },
                { type: "마", usage: "자동차관련시설", emission: 0.046 },
                { type: "마", usage: "창고시설", emission: 0.046 },
                { type: "바", usage: "숙박시설", emission: 0.093 },
                { type: "사", usage: "공장", emission: 0.065 },
                { type: "아", usage: "위험물저장 및 처리시설", emission: 0.095 },
                { type: "자", usage: "의료시설", emission: 0.123 },
                { type: "차", usage: "관광휴게·위탁시설", emission: 0.114 },
                { type: "카", usage: "종교시설", emission: 0.032 },
                { type: "타", usage: "기타 비주거용 시설", emission: 0.059 },
            ],
        };
    },
    computed: {
        subtitleText() {
            try {
                if (!this.data) {
                    return `단위면적당 탄소배출량 (kgCO2/m² · ${this.timeLabel || ''})`;
                }
                
                if (!this.data.values) {
                    return `단위면적당 탄소배출량 (kgCO2/m² · ${this.timeLabel || ''})`;
                }
                
                if (!this.data.values.ghgEmission) {
                    return `단위면적당 탄소배출량 (kgCO2/m² · ${this.timeLabel || ''})`;
                }
                
                const ghgEmission = this.data.values.ghgEmission;
                if (!ghgEmission.unitInfo) {
                    return `단위면적당 탄소배출량 (kgCO2/m² · ${this.timeLabel || ''})`;
                }
                
                const unitInfo = ghgEmission.unitInfo;
                const unit = (unitInfo && unitInfo.unit) ? unitInfo.unit : 'kgCO2/m²';
                
                return `단위면적당 탄소배출량 (${unit} · ${this.timeLabel || ''})`;
            } catch (error) {
                console.warn('[BuildingEmissionCapChart] Error in subtitleText computed:', error);
                return `단위면적당 탄소배출량 (kgCO2/m² · ${this.timeLabel || ''})`;
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.renderChart();
        });
    },
    beforeDestroy() {
        if (this.resizeHandler) {
            window.removeEventListener("resize", this.resizeHandler);
            this.resizeHandler = null;
        }
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    destroyed() {},
    watch: {
        data: {
            handler() {
                // DOM이 준비될 때까지 대기
                this.$nextTick(() => {
                    let retryCount = 0;
                    const maxRetries = 10;
                    
                    // DOM 요소가 존재하고 크기가 0이 아닐 때까지 대기
                    const checkAndRender = () => {
                        if (retryCount >= maxRetries) {
                            console.error("[BuildingEmissionCapChart] Max retry count reached in watcher. DOM element not ready.");
                            return;
                        }
                        
                        if (!this.$refs.gaugeChart) {
                            retryCount++;
                            console.warn(`[BuildingEmissionCapChart] DOM element not ready in watcher, retrying... (${retryCount}/${maxRetries})`);
                            setTimeout(checkAndRender, 100);
                            return;
                        }
                        
                        const element = this.$refs.gaugeChart;
                        const rect = element.getBoundingClientRect();
                        if (rect.width === 0 || rect.height === 0) {
                            retryCount++;
                            console.warn(`[BuildingEmissionCapChart] DOM element has zero size in watcher, retrying... (${retryCount}/${maxRetries})`);
                            setTimeout(checkAndRender, 100);
                            return;
                        }
                        
                        // DOM이 준비되었으면 차트 렌더링
                        this.renderChart();
                    };
                    
                    checkAndRender();
                });
            },
            deep: true,
            immediate: false,
        },
    },
    methods: {
        resizeChart() {
            if (this.chart && !this.chart.isDisposed()) {
                this.chart.resize();
            }
        },
        renderChart() {
            // DOM 요소 확인
            if (!this.$refs.gaugeChart) {
                console.warn("[BuildingEmissionCapChart] DOM element not ready");
                return;
            }

            // DOM 요소가 실제로 렌더링되었는지 확인 (크기가 0이 아니어야 함)
            const element = this.$refs.gaugeChart;
            if (!element) {
                console.warn("[BuildingEmissionCapChart] DOM element not found");
                return;
            }
            
            const rect = element.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) {
                if (this.renderRetryCount < this.maxRetryCount) {
                    this.renderRetryCount++;
                    console.warn(`[BuildingEmissionCapChart] DOM element has zero size, retrying... (${this.renderRetryCount}/${this.maxRetryCount})`);
                    // 다음 프레임에서 다시 시도
                    this.$nextTick(() => {
                        setTimeout(() => this.renderChart(), 100);
                    });
                } else {
                    console.error("[BuildingEmissionCapChart] Max retry count reached. DOM element still has zero size.");
                }
                return;
            }
            
            // 성공적으로 렌더링되면 재시도 카운터 리셋
            this.renderRetryCount = 0;

            // 데이터 검증
            if (!this.data || !this.data.values || !this.data.values.ghgEmission) {
                console.warn("[BuildingEmissionCapChart] No data or incomplete data for rendering chart.");
                if (this.chart) {
                    this.chart.dispose();
                    this.chart = null;
                }
                return;
            }

            if (this.chart) {
                this.chart.dispose();
                this.chart = null;
            }
            
            // echarts.init 호출 전 최종 DOM 유효성 검사
            const chartElement = this.$refs.gaugeChart;
            if (!chartElement) {
                console.error("[BuildingEmissionCapChart] Chart element not found before echarts.init");
                return;
            }
            
            const elementRect = chartElement.getBoundingClientRect();
            if (elementRect.width === 0 || elementRect.height === 0) {
                console.error("[BuildingEmissionCapChart] Chart element has zero size before echarts.init");
                return;
            }
            
            // DOM 요소가 실제 DOM에 연결되어 있는지 확인
            if (!document.contains(chartElement)) {
                console.error("[BuildingEmissionCapChart] Chart element is not in the DOM");
                return;
            }
            
            try {
                this.chart = echarts.init(chartElement);
                if (!this.chart) {
                    console.error("[BuildingEmissionCapChart] echarts.init returned null");
                    return;
                }
            } catch (error) {
                console.error("[BuildingEmissionCapChart] Failed to initialize chart:", error);
                this.chart = null;
                return;
            }

            const ghg = this.data.values.ghgEmission;
            
            // 필수 데이터 검증
            if (!ghg.ticks || !Array.isArray(ghg.ticks) || ghg.ticks.length === 0) {
                console.warn("[BuildingEmissionCapChart] Invalid ticks data:", ghg.ticks);
                return;
            }
            
            if (ghg.buildingGHG === null || ghg.buildingGHG === undefined) {
                console.warn("[BuildingEmissionCapChart] buildingGHG is null or undefined");
                return;
            }
            
            if (ghg.baseline === null || ghg.baseline === undefined) {
                console.warn("[BuildingEmissionCapChart] baseline is null or undefined");
                return;
            }

            const tickValues = ghg.ticks;
            const minVal = tickValues[0];
            const maxVal = tickValues[tickValues.length - 1];
            const splitNum = tickValues.length - 1;
            const pointerValue = ghg.buildingGHG;
            const baseline = ghg.baseline;

            const orangeColors = [
                "#FFE5E3",
                "#FFCBC8",
                "#FFB1AD",
                "#FF9792",
                "#FF7D77",
                "#FF635C",
                "#FF4941",
                "#FF493B",
                "#FF2F26",
            ];
            const colorSteps = orangeColors.map((color, idx) => {
                return [(idx + 1) / orangeColors.length, color];
            });

            const option = {
                tooltip: {
                    formatter: "{a} <br/>{b} : {c}",
                },
                grid: {
                    top: 10,
                },
                series: [
                    {
                        name: "Score",
                        type: "gauge",
                        startAngle: 180,
                        endAngle: 0,
                        min: minVal,
                        max: maxVal,
                        splitNumber: splitNum,
                        radius: "120%",
                        center: ["50%", "80%"],
                        progress: {
                            show: false,
                        },
                        tooltip: {
                            show: false,
                        },
                        axisLine: {
                            lineStyle: {
                                width: 70,
                                color: colorSteps,
                            },
                        },
                        pointer: {
                            show: true,
                            length: "100%",
                            width: 6,
                            itemStyle: {
                                color: "#ffffff",
                                borderColor: "#BDBDBD",
                                borderWidth: 1,
                                shadowColor: "rgba(0, 0, 0, 0.3)",
                                shadowBlur: 5,
                            },
                        },
                        axisTick: {
                            show: true,
                            splitNumber: 1,
                            lineStyle: {
                                width: 2,
                                color: "#fff",
                            },
                        },
                        splitLine: {
                            show: true,
                            length: 10,
                            lineStyle: {
                                width: 3,
                                color: "#fff",
                            },
                        },
                        axisLabel: {
                            show: true,
                            distance: -45,
                            formatter: function (value) {
                                return value ? value.toFixed(1) : "";
                            },
                            color: "#000",
                            fontSize: 12,
                        },
                        detail: {
                            show: false,
                        },
                        data: [
                            {
                                value: pointerValue,
                            },
                        ],
                    },
                ],
            };

            const centerX = this.chart.getWidth() / 2;
            const centerY = this.chart.getHeight() * 0.8;
            const radius = (this.chart.getHeight() * 1.2) / 2;

            const percent = (baseline - minVal) / (maxVal - minVal);
            const rad = Math.PI * (1 - percent);
            const endX = centerX + radius * Math.cos(rad);
            const endY = centerY - radius * Math.sin(rad);

            option.graphic = [
                {
                    type: "line",
                    shape: {
                        x1: centerX,
                        y1: centerY - 20,
                        x2: endX,
                        y2: endY - 7,
                    },
                    style: {
                        stroke: "#00000022",
                        lineWidth: 2,
                    },
                    z: 10,
                },
            ];

            this.chart.setOption(option);
            
            // resize 이벤트 리스너 등록
            if (this.resizeHandler) {
                window.removeEventListener("resize", this.resizeHandler);
            }
            this.resizeHandler = this.resizeChart.bind(this);
            window.addEventListener("resize", this.resizeHandler);
        },
    },
};
</script>

<style scoped>
.chart {
    width: 100%;
    /* height: 155px; */
}
.titleContainer {
    font-size: 18px;
    color: #4d4d4d;
    font-weight: 800;
    display: flex;
    /* flex-direction: column; */
    justify-content: center;
    align-items: center;
}
.common-subtitle {
    font-size: 11px;
}
</style>
